import { useState } from 'react';
import { AtSign, Building, Eye, EyeOff, IdCard, KeyRound, Lock, Phone, ShieldCheck, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ROLE_LABELS, USER_ROLES, type UserRole } from '@/lib/constants/roles';
import { useAuthStore } from '@/lib/auth/store';
import { useCurrentPassword, useSelfResetPassword, useUserDetails } from '../api';
import type { UserProfile as UserProfileType } from '../types';

export function UserProfilePage() {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const profile = useUserDetails(userId);
  

  return (
    <ScrollPage>
      <PageHeader
        title="Your profile"
        description="Account information and balances."
      />

       <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardContent className="p-6">
            {profile.isLoading ? (
              <ProfileSkeleton />
            ) : profile.data ? (
              <ProfileDetails data={profile.data} role={role} />
            ) : (
              <p className="text-sm text-muted-foreground">Could not load your profile.</p>
            )}
          </CardContent>
        </Card>

         {role === 'ROLE_USER' || role === USER_ROLES.SUB_SUPER_ADMIN ? (
          <ChangePasswordCard userId={userId ? Number(userId) : null} />
        ) : (
          <Card>
            <CardContent className="space-y-4 p-6">
              <h3 className="text-sm font-semibold">Balances</h3>
              <BalanceRow
                icon={<Wallet className="h-4 w-4" />}
                label="Voice balance"
                value={profile.data?.credits}
                loading={profile.isLoading}
              />
              <BalanceRow
                icon={<Wallet className="h-4 w-4" />}
                label="TTS balance"
                value={profile.data?.ttsCredits}
                loading={profile.isLoading}
              />
              {profile.data?.planName && (
                <div className="rounded-md bg-muted/40 px-3 py-2">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Plan
                  </div>
                  <div className="text-sm font-medium text-foreground">{profile.data.planName}</div>
                </div>
              )}
              {profile.data?.ttsPlanName && (
                <div className="rounded-md bg-muted/40 px-3 py-2">
                  <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                    TTS plan
                  </div>
                  <div className="text-sm font-medium text-foreground">{profile.data.ttsPlanName}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Balances card for User role — below the grid */}
      {(role === 'ROLE_USER' || role === USER_ROLES.SUB_SUPER_ADMIN) && (
       <Card className="mt-4">
          <CardContent className="p-6">
            <h3 className="mb-4 text-sm font-semibold">Balances</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <BalanceRow
              icon={<Wallet className="h-4 w-4" />}
              label="Voice balance"
              value={profile.data?.credits}
              loading={profile.isLoading}
            />
            <BalanceRow
              icon={<Wallet className="h-4 w-4" />}
              label="TTS balance"
              value={profile.data?.ttsCredits}
              loading={profile.isLoading}
            />
            {profile.data?.planName && (
              <div className="rounded-md bg-muted/40 px-3 py-2">
                <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Plan
                </div>
                <div className="text-sm font-medium text-foreground">{profile.data.planName}</div>
              </div>
            )}
            {profile.data?.ttsPlanName && (
              <div className="rounded-md bg-muted/40 px-3 py-2">
                <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                  TTS plan
                </div>
                <div className="text-sm font-medium text-foreground">{profile.data.ttsPlanName}</div>
              </div>
            )}
            </div>
          </CardContent>
        </Card>
      )}
    </ScrollPage>
  );
}

function ChangePasswordCard({ userId }: { userId: number | null }) {
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
 const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);  const currentPwd = useCurrentPassword(userId);
  const reset = useSelfResetPassword();

  const handleSubmit = () => {
    if (!userId) return;
    if (newPass.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (newPass !== confirmPass) {
      toast.error('Passwords do not match.');
      return;
    }
    reset.mutate(
      { userId, password: newPass },
      {
        onSuccess: () => {
          toast.success('Password changed successfully.');
          setNewPass('');
          setConfirmPass('');
          currentPwd.refetch();
        },
        onError: () => toast.error('Could not change password.'),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <KeyRound className="h-4 w-4" />
          Reset Password
        </CardTitle>
        <CardDescription>Reset your account password.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Current password (read-only) */}
         <div className="space-y-1.5">
          <Label>Current password</Label>
          <div className="relative">
            <Lock className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type={showCurrent ? 'text' : 'password'}
              className="pl-8 pr-9"
              value={currentPwd.data?.password ?? '••••••'}
              readOnly
            />
            <button
              type="button"
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
              onClick={() => setShowCurrent((v) => !v)}
              tabIndex={-1}
            >
              {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* New password */}
        <div className="space-y-1.5">
          <Label htmlFor="new-pass">New password</Label>
          <div className="relative">
            <Input
              id="new-pass"
              type={showNew ? 'text' : 'password'}
              className="pr-9"
              placeholder="Min 6 characters"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
              onClick={() => setShowNew((v) => !v)}
              tabIndex={-1}
            >
              {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Confirm password */}
        <div className="space-y-1.5">
          <Label htmlFor="confirm-pass">Confirm password</Label>
          <Input
            id="confirm-pass"
            type={showNew ? 'text' : 'password'}
            placeholder="Re-enter password"
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
          />
        </div>

        {newPass && confirmPass && newPass !== confirmPass && (
          <p className="text-xs text-destructive">Passwords do not match.</p>
        )}
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={reset.isPending || !newPass || !confirmPass}
        >
          {reset.isPending ? 'Saving…' : 'Reset Password'}
        </Button>
      </CardContent>
    </Card>
  );
}

function ProfileDetails({ data, role }: { data: UserProfileType; role: UserRole | null }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{data.name || data.username}</h2>
          <p className="text-sm text-muted-foreground">@{data.username}</p>
        </div>
        {role && <Badge variant="outline">{ROLE_LABELS[role]}</Badge>}
      </div>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field icon={<AtSign className="h-4 w-4" />} label="Email" value={data.emailid} />
        <Field icon={<Phone className="h-4 w-4" />} label="Phone" value={data.number} />
        <Field icon={<Building className="h-4 w-4" />} label="Company" value={data.company} />
        {data.parentName && (
          <Field
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Parent name"
            value={data.parentName}
          />
        )}
        {data.expiryDate && (
          <Field
            icon={<IdCard className="h-4 w-4" />}
            label="Validity"
            value={data.expiryDate}
          />
        )}
      </dl>
    </div>
  );
}

function Field({ icon, label, value }: { icon: React.ReactNode; label: string; value?: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div>
        <dt className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </dt>
        <dd className="text-sm font-medium text-foreground">{value ?? '—'}</dd>
      </div>
    </div>
  );
}

function BalanceRow({
  icon,
  label,
  value,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number;
  loading?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-md border bg-card px-3 py-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        {loading ? (
          <Skeleton className="mt-1 h-4 w-16" />
        ) : (
          <div className="text-sm font-semibold tabular-nums text-foreground">
            ₹{Number(value ?? 0).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-6 w-24" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    </div>
  );
}
